import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authAPI, errorText, AUTH_EXPIRED_EVENT } from '../services/api';

interface User {
  id: number;
  username: string;
  created_at: string;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  isAuthenticated: boolean;
  login: (username: string, password: string) => Promise<{ success: boolean; message?: string }>;
  register: (username: string, password: string) => Promise<{ success: boolean; message?: string }>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    checkAuth();
  }, []);

  // 任意一个请求撞上"令牌失效"，就在这里把登录态清掉，让路由自己跳登录页。
  // 拦截器不在 React 树里，所以它只能广播，不能直接改状态。
  useEffect(() => {
    const handleExpired = () => {
      setUser(null);
      setIsAuthenticated(false);
      setLoading(false);
    };
    window.addEventListener(AUTH_EXPIRED_EVENT, handleExpired);
    return () => window.removeEventListener(AUTH_EXPIRED_EVENT, handleExpired);
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');

    if (!token) {
      setIsAuthenticated(false);
      setLoading(false);
      return;
    }

    // 先从localStorage恢复用户状态，提供快速响应
    if (savedUser) {
      try {
        setUser(JSON.parse(savedUser));
        setIsAuthenticated(true);
        // 这里已经能确定要渲染什么了，所以不再等 /api/auth/me 回来。
        //
        // 原来 loading 一直是 true 到那个请求落地，而 App.tsx 在 loading 期间只渲染
        // 一个全屏 Spin —— 任何路由都不挂载，于是 /api/dashboard/overview 必须排在
        // 这次往返之后才能发出。而 /api/auth/me 只是一次 JWT 解码加一次用户查询，
        // 纯延迟、不产出任何这里还不知道的信息。axios 的超时是 300 秒，所以它一卡住
        // 用户就要对着那个 Spin 看五分钟。
        //
        // 下面的请求仍然会发，只是变成后台校验：失败就清状态，路由自己跳登录页。
        setLoading(false);
      } catch (error) {
        localStorage.removeItem('user');
        setIsAuthenticated(false);
      }
    }

    try {
      const response = await authAPI.getCurrentUser();
      if (response.data.success) {
        setUser(response.data.user);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        setIsAuthenticated(true);
      } else {
        // API调用失败，清除状态
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setUser(null);
        setIsAuthenticated(false);
      }
    } catch (error) {
      // API调用失败，清除状态
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  const login = async (username: string, password: string) => {
    try {
      const response = await authAPI.login(username, password);
      if (response.data.success && response.data.user && response.data.access_token) {
        // 立即更新状态和本地存储
        localStorage.setItem('token', response.data.access_token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        setUser(response.data.user);
        setIsAuthenticated(true);
        return { success: true };
      } else {
        return { success: false, message: '登录响应无效' };
      }
    } catch (error: any) {
      // detail 在 422 时是一个对象数组，直接交给 message.error 会抛异常、
      // 结果一个提示都不显示。errorText 负责压成字符串。
      return { success: false, message: errorText(error, '登录失败') };
    }
  };

  const register = async (username: string, password: string) => {
    try {
      const response = await authAPI.register(username, password);
      if (response.data.success) {
        return { success: true };
      } else {
        return { success: false, message: response.data.message || '注册失败' };
      }
    } catch (error: any) {
      return { success: false, message: errorText(error, '注册失败') };
    }
  };

  const logout = () => {
    // 立即清除状态和本地存储
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setIsAuthenticated(false);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        isAuthenticated,
        login,
        register,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};